import { createAsyncThunk } from '@reduxjs/toolkit';
import { FeedbackCreateRequest } from '../../types/chat.types';
import { AgentConfig } from '../../types/agent.types';
import { getAccessTokenForAgent } from '../../auth/getAccessTokenForAgent';
import { createAgentApiClient } from '../../api/apiClient';
import { createAgentApi, AgentApi, MessageResponse } from '../../api/agentApi';
import { RootState } from '../store';

/**
 * Cache of AgentApi instances keyed by agent ID.
 * Avoids recreating Axios clients + interceptors on every thunk dispatch.
 */
const apiCache = new Map<string, AgentApi>();

function getApiForAgent(agent: AgentConfig): AgentApi {
  const cached = apiCache.get(agent.id);
  if (cached) return cached;

  const client = createAgentApiClient(agent.apiEndpoint, () =>
    getAccessTokenForAgent(agent.requiredScopes),
  );
  const api = createAgentApi(client);
  apiCache.set(agent.id, api);
  return api;
}

// --- Create Session ---
export const createSessionThunk = createAsyncThunk<
  { sessionId: string; agentId: string },
  void,
  { state: RootState; rejectValue: string }
>('session/createSession', async (_, { getState, rejectWithValue }) => {
  const agent = getState().agent.activeAgent;
  if (!agent) return rejectWithValue('No active agent');

  try {
    const api = getApiForAgent(agent);
    const response = await api.createSession();
    return { sessionId: response.session_id, agentId: agent.id };
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Failed to create session';
    return rejectWithValue(message);
  }
});

// --- Send Message ---
export const sendMessageThunk = createAsyncThunk<
  MessageResponse,
  { message: string; apiMessage?: string },
  { state: RootState; rejectValue: string }
>(
  'session/sendMessage',
  async ({ message, apiMessage }, { getState, rejectWithValue }) => {
    const state = getState();
    const agent = state.agent.activeAgent;
    const sessionId = state.session.activeSessionId;

    if (!agent) return rejectWithValue('No active agent');
    if (!sessionId) return rejectWithValue('No active session');

    try {
      const api = getApiForAgent(agent);
      // apiMessage carries the tagged payload sent to the API;
      // message is the clean display text stored in session state.
      return await api.sendMessage(sessionId, apiMessage ?? message);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to send message';
      return rejectWithValue(msg);
    }
  },
);

// --- Submit Feedback ---
export const submitFeedbackThunk = createAsyncThunk<
  void,
  FeedbackCreateRequest,
  { state: RootState; rejectValue: string }
>(
  'session/submitFeedback',
  async (feedbackData, { getState, rejectWithValue }) => {
    const state = getState();
    const agent = state.agent.activeAgent;
    const sessionId = state.session.activeSessionId;

    if (!agent) return rejectWithValue('No active agent');
    if (!sessionId) return rejectWithValue('No active session');

    try {
      const api = getApiForAgent(agent);
      await api.submitFeedback(sessionId, feedbackData);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to submit feedback';
      return rejectWithValue(msg);
    }
  },
);

// --- Fetch Visualization Data (silent — does not update message history) ---
export const fetchVisualizationDataThunk = createAsyncThunk<
  string, // raw response text from the agent
  string, // the predefined prompt to send
  { state: RootState; rejectValue: string }
>(
  'session/fetchVisualizationData',
  async (prompt, { getState, dispatch, rejectWithValue }) => {
    const state = getState();
    const agent = state.agent.activeAgent;
    let sessionId = state.session.activeSessionId;

    if (!agent) return rejectWithValue('No active agent');

    // Create a session if none exists yet
    if (!sessionId) {
      try {
        const result = await dispatch(createSessionThunk()).unwrap();
        sessionId = result.sessionId;
      } catch {
        return rejectWithValue('Failed to create session for visualization');
      }
    }

    try {
      const api = getApiForAgent(agent);
      const response = await api.sendMessage(sessionId!, prompt);
      return response.message;
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Failed to fetch visualization data';
      return rejectWithValue(msg);
    }
  },
);

// --- Delete Session ---
export const deleteSessionThunk = createAsyncThunk<
  string, // returns the deleted session ID
  void,
  { state: RootState; rejectValue: string }
>('session/deleteSession', async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const agent = state.agent.activeAgent;
  const sessionId = state.session.activeSessionId;

  if (!agent) return rejectWithValue('No active agent');
  if (!sessionId) return rejectWithValue('No active session');

  try {
    const api = getApiForAgent(agent);
    await api.deleteSession(sessionId);
    return sessionId;
  } catch (error) {
    const msg =
      error instanceof Error ? error.message : 'Failed to delete session';
    return rejectWithValue(msg);
  }
});
